import { Formik, Field, Form, FormikHelpers, FieldProps } from "formik";
import { useTranslation } from "react-i18next";
import { FormControl, FormErrorMessage } from "@chakra-ui/form-control";
import { Stack, ModalFooter, Textarea, Button } from "@chakra-ui/react";
import { Input } from "@chakra-ui/input";
import { Label } from "../../components/Label";
import { useCallback } from "react";
import { GenericObjective } from "../../types";
import * as Yup from "yup";
import { PrimayButton } from "../Button";

interface Values {
  id: string;
  title: string;
  description: string;
}

type GenericObjectiveFormProps = {
  onSubmit: (data: any) => any;
  genericObjective?: GenericObjective | null;
  onCancel: () => any;
  editable: boolean;
};

export const ObjectiveForm: React.FC<GenericObjectiveFormProps> = ({
  onSubmit,
  genericObjective,
  onCancel,
  editable,
}) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
      setSubmitting(false);
      onSubmit(values);
    },
    [onSubmit]
  );

  return (
    <Formik
      initialValues={{
        id: genericObjective?.id ? genericObjective?.id : "",
        title: genericObjective?.title ? genericObjective?.title : "",
        description: genericObjective?.description
          ? genericObjective?.description
          : "",
      }}
      enableReinitialize
      onSubmit={handleSubmit}
      validationSchema={Yup.object().shape({
        title: Yup.string().required(
          t("screens.client.genericObjectiveFormValidations.titleRequired")
        ),
        description: Yup.string().required(
          t(
            "screens.client.genericObjectiveFormValidations.descriptionRequired"
          )
        ),
      })}
    >
      {(props) => (
        <Form
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Field id={"id"} name={"id"}>
            {({ field, form: { setFieldValue } }: FieldProps) => (
              <FormControl>
                <Input
                  size="md"
                  type={"hidden"}
                  name={field.name}
                  value={field.value}
                  onChange={(event) =>
                    setFieldValue(field.name, event.target.value)
                  }
                  disabled={!editable}
                />
              </FormControl>
            )}
          </Field>

          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"title"} name={"title"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label>
                    {t("screens.client.genericObjectiveForm.title")}
                  </Label>
                  <Input
                    size="md"
                    type={"text"}
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                    disabled={!editable}
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>
          <Stack spacing={"1rem"} direction={["column", "row"]} mb={"20px"}>
            <Field id={"description"} name={"description"}>
              {({ field, form: { setFieldValue }, meta }: FieldProps) => (
                <FormControl isInvalid={Boolean(meta.error && meta.touched)}>
                  <Label>
                    {t("screens.client.genericObjectiveForm.description")}
                  </Label>
                  <Textarea
                    size="md"
                    type={"text"}
                    name={field.name}
                    value={field.value}
                    onChange={(event) =>
                      setFieldValue(field.name, event.target.value)
                    }
                    disabled={!editable}
                  />
                  <FormErrorMessage>{meta.error}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </Stack>
          <ModalFooter>
            {editable ? (
              <>
                <Button type={"reset"} variant={"ghost"} onClick={onCancel}>
                  {t("common.reset")}
                </Button>
                <PrimayButton type={"submit"} isLoading={false}>
                  {t("common.save")}
                </PrimayButton>
              </>
            ) : (
              <PrimayButton type={"reset"} isLoading={false} onClick={onCancel}>
                {t("common.close")}
              </PrimayButton>
            )}
          </ModalFooter>
        </Form>
      )}
    </Formik>
  );
};
